import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import * as yup from 'yup';

// @mui
import {styled, alpha} from '@mui/material/styles';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Alert from '@mui/material/Alert';
import Fade from '@mui/material/Fade';
import Grid from '@mui/material/Grid';
import InputAdornment from '@mui/material/InputAdornment';

// icons
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';

// constants
import {ERROR_MESSAGES} from '../constants/reservation';

// ----------------------------------------------------------------------

const FormField = styled(TextField)(({theme}) => ({
  'marginBottom': theme.spacing(2),
  '& .MuiOutlinedInput-root': {
    'transition': theme.transitions.create(['box-shadow', 'border-color']),
    '&:hover': {
      borderColor: theme.palette.primary.main,
    },
    '&.Mui-focused': {
      boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.25)}`,
    },
  },
}));

// ----------------------------------------------------------------------

export function ContactStep({
  formData,
  updateFormData,
  config,
  setValidation,
  setErrors,
  errors,
  setStepReady,
}) {
  // State to track which fields have been touched by the user
  const [touchedFields, setTouchedFields] = useState({
    firstName: false,
    lastName: false,
    email: false,
    phone: false,
  });

  // Define validation schema with Yup
  const validationSchema = React.useMemo(() => yup.object({
    firstName: yup.string().required(ERROR_MESSAGES.FIRST_NAME_REQUIRED).trim(),
    lastName: yup.string().required(ERROR_MESSAGES.LAST_NAME_REQUIRED).trim(),
    email: yup.string()
        .required(ERROR_MESSAGES.EMAIL_REQUIRED)
        .matches(
            /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
            ERROR_MESSAGES.EMAIL_INVALID,
        )
        .trim(),
    phone: yup.string()
        .required(ERROR_MESSAGES.PHONE_REQUIRED)
    // Add a regex pattern for phone validation
        .matches(
            /^(\+\d{1,3}[- ]?)?\d{8,14}$/,
            ERROR_MESSAGES.PHONE_INVALID,
        )
        .trim(),
  }), []);

  // Pré-remplir les champs si disponibles dans la config
  useEffect(() => {
    // Si on a des infos utilisateur dans la config
    if (config?.userDefaults) {
      const {firstName, lastName, email, phone} = config.userDefaults;

      if (firstName && !formData.firstName) {
        updateFormData('firstName', firstName);
      }

      if (lastName && !formData.lastName) {
        updateFormData('lastName', lastName);
      }

      if (email && !formData.email) {
        updateFormData('email', email);
      }

      if (phone && !formData.phone) {
        updateFormData('phone', phone);
      }
    }
  }, [config, updateFormData, formData]);

  // Validate a single field with Yup
  const validateField = async (field, value) => {
    try {
      // Create a partial schema for the specific field
      await yup.reach(validationSchema, field).validate(value);

      // Clear error if validation passes
      if (errors[field]) {
        const newErrors = {...errors};
        delete newErrors[field];
        setErrors(newErrors);
      }
      return true;
    } catch (err) {
      // Set error message from Yup validation
      setErrors((prev) => ({
        ...prev,
        [field]: err.message,
      }));
      return false;
    }
  };

  // Handle field blur event - validate when user leaves a field
  const handleBlur = (field) => () => {
    setTouchedFields((prev) => ({
      ...prev,
      [field]: true,
    }));

    validateField(field, formData[field] || '');
  };

  // Handle input change
  const handleChange = (field) => (event) => {
    const value = event.target.value;
    updateFormData(field, value);

    // If the field has been touched, revalidate on change
    if (touchedFields[field]) {
      validateField(field, value);
    }
  };

  // Complete form validation using Yup
  const validateStep = React.useCallback(async () => {
    try {
      await validationSchema.validate(formData, {abortEarly: false});
      return true;
    } catch (err) {
      // Transform Yup errors to our error format
      const yupErrors = {};

      if (err.inner) {
        err.inner.forEach((e) => {
          yupErrors[e.path] = e.message;
        });
      }

      setErrors(yupErrors);
      return false;
    }
  }, [formData, validationSchema, setErrors]);

  // Register validation function
  useEffect(() => {
    setValidation(() => validateStep);
  }, [setValidation, validateStep]);

  // Effect to control next button state based on form validity
  useEffect(() => {
    if (setStepReady) {
      // Check if all required fields are filled and valid
      validationSchema
          .isValid(formData)
          .then((isValid) => {
            setStepReady(isValid);
          });
    }
  }, [formData, validationSchema, setStepReady]);

  return (
    <Fade in timeout={400}>
      <Box>
        {/* Formulaire de contact */}
        <Typography variant="h6" sx={{mb: 3}}>
          Vos coordonnées
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <FormField
              fullWidth
              label="Prénom"
              value={formData.firstName || ''}
              onChange={handleChange('firstName')}
              onBlur={handleBlur('firstName')}
              error={!!errors.firstName}
              helperText={errors.firstName}
              required
              size="medium"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonIcon fontSize="small" color="primary" />
                  </InputAdornment>
                ),
              }}
              variant="outlined"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormField
              fullWidth
              label="Nom"
              value={formData.lastName || ''}
              onChange={handleChange('lastName')}
              onBlur={handleBlur('lastName')}
              error={!!errors.lastName}
              helperText={errors.lastName}
              required
              size="medium"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonIcon fontSize="small" color="primary" />
                  </InputAdornment>
                ),
              }}
              variant="outlined"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormField
              fullWidth
              label="Email"
              type="email"
              value={formData.email || ''}
              onChange={handleChange('email')}
              onBlur={handleBlur('email')}
              error={!!errors.email}
              helperText={errors.email}
              required
              size="medium"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon fontSize="small" color="primary" />
                  </InputAdornment>
                ),
              }}
              variant="outlined"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormField
              fullWidth
              label="Téléphone"
              type="tel"
              value={formData.phone || ''}
              onChange={handleChange('phone')}
              onBlur={handleBlur('phone')}
              error={!!errors.phone}
              helperText={errors.phone}
              required
              size="medium"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PhoneIcon fontSize="small" color="primary" />
                  </InputAdornment>
                ),
              }}
              variant="outlined"
            />
          </Grid>
        </Grid>

        {config?.cancellationPolicy?.applyCancellationFee && (
          <Alert severity="info" sx={{mt: 3}} variant="outlined">
            <Typography variant="body2">
              <strong>Politique d&apos;annulation :</strong> Des frais peuvent s&apos;appliquer en cas d&apos;annulation à moins de {config.cancellationPolicy.minimumHours}h avant la réservation.
            </Typography>
          </Alert>
        )}
      </Box>
    </Fade>
  );
}

ContactStep.propTypes = {
  formData: PropTypes.object.isRequired,
  updateFormData: PropTypes.func.isRequired,
  config: PropTypes.object,
  setValidation: PropTypes.func.isRequired,
  setErrors: PropTypes.func.isRequired,
  errors: PropTypes.object,
  setStepReady: PropTypes.func,
};
